import type { DocumentValidationRowDto } from '@app/contracts';
import { CheckCircle2, ShieldCheck, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useDocumentValidationDecide } from '../hooks/useDocumentValidationDecide.js';

function statusLabel(v: number): string {
  if (v === 0) return 'En attente';
  if (v === 1) return 'Validé';
  if (v === 2) return 'Refusé';
  return `État ${v}`;
}

type Props = {
  documentId: string;
  canValidate: boolean;
  validationRow: DocumentValidationRowDto;
};

export function InlineDocumentValueValidation({ documentId, canValidate, validationRow }: Props) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const decide = useDocumentValidationDecide(documentId);

  useEffect(() => {
    if (validationRow.validation !== 0) {
      setRejectOpen(false);
      setRejectComment('');
    }
  }, [validationRow.validation]);

  if (!canValidate) return null;

  const busy = decide.isPending;

  return (
    <div className="border-t border-amber-200/70 bg-amber-50/60 px-4 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-900/90">
          <ShieldCheck className="h-3.5 w-3.5 shrink-0" aria-hidden />
          Validation
        </span>
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            validationRow.validation === 0
              ? 'bg-amber-100 text-amber-900'
              : validationRow.validation === 1
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-rose-100 text-rose-800'
          }`}
        >
          {statusLabel(validationRow.validation)}
        </span>
      </div>

      {validationRow.content ? (
        <p className="mt-1.5 whitespace-pre-wrap text-[11px] text-slate-600">
          {validationRow.content}
        </p>
      ) : null}

      {validationRow.validation === 0 ? (
        <div className="mt-2">
          {rejectOpen ? (
            <div className="flex flex-col gap-2">
              <label className="text-xs font-medium text-slate-600">
                Motif du refus
                <textarea
                  className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-800 outline-none focus:border-rose-400"
                  rows={2}
                  value={rejectComment}
                  onChange={(e) => setRejectComment(e.target.value)}
                  placeholder="Expliquez ce qui doit être corrigé…"
                />
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    const c = rejectComment.trim();
                    if (!c) {
                      alert('Le commentaire est obligatoire pour refuser.');
                      return;
                    }
                    decide.mutate({
                      id: documentId,
                      validationId: validationRow.id,
                      data: { decision: 'reject', comment: c },
                    });
                  }}
                  className="inline-flex items-center gap-1 rounded-lg bg-rose-600 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
                >
                  <XCircle className="h-3 w-3" />
                  Confirmer le refus
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setRejectOpen(false);
                    setRejectComment('');
                  }}
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
                >
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  decide.mutate({
                    id: documentId,
                    validationId: validationRow.id,
                    data: { decision: 'approve' },
                  })
                }
                className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                <CheckCircle2 className="h-3 w-3" />
                Approuver
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setRejectOpen(true);
                  setRejectComment('');
                }}
                className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11px] font-semibold text-rose-800 hover:bg-rose-100 disabled:opacity-50"
              >
                <XCircle className="h-3 w-3" />
                Refuser…
              </button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
